import {
  Building2,
  ChevronRight,
  FileText,
  Info,
  LogOut,
  Monitor,
  Settings,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { T } from "../theme"
import { Avatar, MButton, MCard, MHeader, SectionLabel, Touchable } from "../primitives"
import type { MobileAccount, MobileUser } from "../types"

interface ProfileScreenProps {
  user: MobileUser
  account: MobileAccount
  appVersion?: string
  onLogout: () => void
  onChangePassword?: () => void
}

// Opt out of the phone-browser routing to /m: remember the choice for a year
// and jump to the desktop app. Inside the installed PWA this is a harmless
// no-op round-trip — the standalone branch in application.html.erb bounces
// desktop routes straight back to /m regardless of this cookie.
// On Android the PWA shares Chrome's cookie jar, so tapping this inside the
// PWA still flips future phone-browser visits to the desktop version.
function switchToDesktop() {
  document.cookie =
    "prefer_desktop=1; path=/; max-age=" + 60 * 60 * 24 * 365 + "; SameSite=Lax"
  window.location.assign("/dashboard")
}

export function ProfileScreen({
  user,
  account,
  appVersion = "0.14.2",
  onLogout,
  onChangePassword,
}: ProfileScreenProps) {
  const { t } = useTranslation()
  return (
    <div>
      <MHeader title={t("mobile.profile.title")} />
      <div style={{ padding: "20px 16px 100px" }}>
        <MCard
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginBottom: 20,
          }}
        >
          <Avatar name={user.name} size={56} />
          <div
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: T.fontDisplay,
              fontSize: 17,
              fontWeight: 600,
              color: T.ink,
              letterSpacing: -0.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user.name}
          </div>
        </MCard>

        <SectionLabel>{t("mobile.profile.account")}</SectionLabel>
        <MCard padded={false} style={{ marginBottom: 18 }}>
          <ProfileRow
            icon={Building2}
            label={t("mobile.profile.organization")}
            value={account.name}
          />
          <ProfileRow
            icon={Settings}
            label={t("mobile.profile.changePassword")}
            onClick={onChangePassword}
            last
          />
        </MCard>

        <SectionLabel>{t("mobile.profile.support")}</SectionLabel>
        <MCard padded={false} style={{ marginBottom: 24 }}>
          <ProfileRow
            icon={Monitor}
            label={t("mobile.profile.useDesktop")}
            onClick={switchToDesktop}
          />
          <ProfileRow icon={Info} label={t("mobile.profile.help")} />
          <ProfileRow icon={FileText} label={t("mobile.profile.terms")} last />
        </MCard>

        <MButton
          variant="ghost"
          fullWidth
          leftIcon={LogOut}
          onClick={onLogout}
          style={{ color: T.danger, borderColor: T.dangerSoft }}
        >
          {t("mobile.profile.logout")}
        </MButton>

        <div
          style={{
            textAlign: "center",
            marginTop: 18,
            fontFamily: T.fontMono,
            fontSize: 10.5,
            color: T.inkMuted,
            letterSpacing: 1,
          }}
        >
          {t("mobile.profile.version", { version: appVersion })}
        </div>
      </div>
    </div>
  )
}

interface ProfileRowProps {
  icon: LucideIcon
  label: string
  value?: string
  onClick?: () => void
  last?: boolean
}

function ProfileRow({ icon: Icon, label, value, onClick, last }: ProfileRowProps) {
  return (
    <Touchable
      onClick={onClick}
      style={{
        padding: "14px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        borderBottom: last ? "none" : `1px solid ${T.divider}`,
      }}
    >
      <div
        style={{
          width: 32, height: 32,
          background: T.surfaceAlt,
          border: `1px solid ${T.border}`,
          display: "grid",
          placeItems: "center",
        }}
      >
        <Icon size={16} color={T.inkSoft} />
      </div>
      <div style={{ flex: 1, fontSize: 15, color: T.ink, fontWeight: 500 }}>{label}</div>
      {value && <div style={{ fontSize: 13, color: T.inkSoft }}>{value}</div>}
      <ChevronRight size={16} color={T.inkMuted} />
    </Touchable>
  )
}
